import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccountListItem } from "../../lib/accounts";
import type { CreatePropertyInput, Property, UpdatePropertyInput } from "../../lib/realestate";

interface Props {
  mode: "create" | "edit";
  initial?: Property;
  accounts: AccountListItem[];
  onSubmit: (input: CreatePropertyInput | UpdatePropertyInput) => Promise<void>;
  submitting?: boolean;
  serverError?: string;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n < 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function formatEuroDe(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

export function PropertyForm({
  mode,
  initial,
  accounts,
  onSubmit,
  submitting,
  serverError,
}: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [street, setStreet] = useState(initial?.street ?? "");
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [purchaseDate, setPurchaseDate] = useState(initial?.purchaseDate ?? todayIso());
  const [purchasePrice, setPurchasePrice] = useState(
    initial ? formatEuroDe(initial.purchasePriceCents) : "",
  );
  const [acquisitionCosts, setAcquisitionCosts] = useState(
    initial ? formatEuroDe(initial.acquisitionCostsCents) : "0,00",
  );
  const [buildingSharePct, setBuildingSharePct] = useState(
    initial ? String(initial.buildingShareBp / 100) : "80",
  );
  const [afaRatePct, setAfaRatePct] = useState(initial ? String(initial.afaRateBp / 100) : "2");
  const [afaStartDate, setAfaStartDate] = useState(initial?.afaStartDate ?? todayIso());
  const [accountId, setAccountId] = useState(initial?.accountId ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    let purchasePriceCents: number;
    let acquisitionCostsCents: number;
    let buildingShareBp: number;
    let afaRateBp: number;
    try {
      purchasePriceCents = parseEuroDe(purchasePrice);
      acquisitionCostsCents = parseEuroDe(acquisitionCosts);
      buildingShareBp = Math.round(Number.parseFloat(buildingSharePct) * 100);
      afaRateBp = Math.round(Number.parseFloat(afaRatePct) * 100);
      if (Number.isNaN(buildingShareBp) || buildingShareBp < 0 || buildingShareBp > 10000)
        throw new Error("invalid_share");
      if (Number.isNaN(afaRateBp) || afaRateBp < 0 || afaRateBp > 1000)
        throw new Error("invalid_afa");
    } catch {
      setLocalError(t("realestate.errors.invalid_amount"));
      return;
    }
    const base: CreatePropertyInput = {
      name: name.trim(),
      street: street.trim(),
      postalCode: postalCode.trim(),
      city: city.trim(),
      purchaseDate,
      purchasePriceCents,
      acquisitionCostsCents,
      buildingShareBp,
      afaRateBp,
      afaStartDate,
    };
    if (accountId) base.accountId = accountId;
    if (notes.trim()) base.notes = notes.trim();
    await onSubmit(base);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.name")}</span>
        <input
          className="block mt-1 w-full border rounded px-2 py-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          maxLength={120}
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.street")}</span>
        <input
          className="block mt-1 w-full border rounded px-2 py-1"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
          required
          maxLength={160}
        />
      </label>
      <div className="grid grid-cols-3 gap-2">
        <label className="block">
          <span className="text-sm font-medium">{t("realestate.form.postal_code")}</span>
          <input
            className="block mt-1 w-full border rounded px-2 py-1"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            required
            maxLength={20}
          />
        </label>
        <label className="col-span-2 block">
          <span className="text-sm font-medium">{t("realestate.form.city")}</span>
          <input
            className="block mt-1 w-full border rounded px-2 py-1"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
            maxLength={120}
          />
        </label>
      </div>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.purchase_date")}</span>
        <input
          type="date"
          className="block mt-1 w-full border rounded px-2 py-1"
          value={purchaseDate}
          max={todayIso()}
          onChange={(e) => setPurchaseDate(e.target.value)}
          required
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.purchase_price")}</span>
        <input
          inputMode="decimal"
          className="block mt-1 w-full border rounded px-2 py-1"
          value={purchasePrice}
          onChange={(e) => setPurchasePrice(e.target.value)}
          required
          placeholder="250.000,00"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.acquisition_costs")}</span>
        <input
          inputMode="decimal"
          className="block mt-1 w-full border rounded px-2 py-1"
          value={acquisitionCosts}
          onChange={(e) => setAcquisitionCosts(e.target.value)}
          placeholder="25.000,00"
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.building_share")}</span>
        <input
          type="number"
          min="0"
          max="100"
          step="0.1"
          className="block mt-1 w-full border rounded px-2 py-1"
          value={buildingSharePct}
          onChange={(e) => setBuildingSharePct(e.target.value)}
          required
        />
        <span className="text-xs text-slate-500">{t("realestate.form.building_share_hint")}</span>
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.afa_rate")}</span>
        <input
          type="number"
          min="0"
          max="10"
          step="0.1"
          className="block mt-1 w-full border rounded px-2 py-1"
          value={afaRatePct}
          onChange={(e) => setAfaRatePct(e.target.value)}
          required
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.afa_start_date")}</span>
        <input
          type="date"
          className="block mt-1 w-full border rounded px-2 py-1"
          value={afaStartDate}
          onChange={(e) => setAfaStartDate(e.target.value)}
          required
        />
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.account")}</span>
        <select
          className="block mt-1 w-full border rounded px-2 py-1"
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">{t("realestate.form.account_none")}</option>
          {accounts
            .filter((a) => a.isActive)
            .map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
        </select>
      </label>
      <label className="block">
        <span className="text-sm font-medium">{t("realestate.form.notes")}</span>
        <textarea
          className="block mt-1 w-full border rounded px-2 py-1"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          rows={3}
        />
      </label>
      {(localError || serverError) && (
        <div className="text-red-700 text-sm">{localError ?? serverError}</div>
      )}
      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded"
        disabled={submitting}
      >
        {submitting
          ? t("common.submitting")
          : mode === "create"
            ? t("common.create")
            : t("common.save")}
      </button>
    </form>
  );
}
